import { useEffect, useState } from 'react';
import { instancesApi, cacheApi } from '../api/endpoints';
import { Tab, TabGroup, TabList, TabPanel, TabPanels } from '@headlessui/react';
import {
  ServerIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  ArrowPathIcon,
  CircleStackIcon,
} from '@heroicons/react/24/outline';
import { formatDate, formatRelativeTime } from '../utils/helpers';
import SpotlightCard from '../components/ui/SpotlightCard';
import { Button } from '../components/ui/Button';
import PageHeader from '../components/ui/PageHeader';
import { CardSkeleton, StatsGridSkeleton, TableRowSkeleton } from '../components/ui/SkeletonPatterns';
import { useInstancesStore } from '../stores/instancesStore';

interface Instance {
  id: string;
  hostname: string;
  region: string;
  started_at: number;
  last_heartbeat: number;
  status: string;
  active_task_count: number;
}

interface CacheInfo {
  provider: string;
  configuredProvider: string;
  config: {
    listLimit: number;
    defaultTtl: number;
  };
  capabilities: {
    canListKeys: boolean;
    canWrite: boolean;
  };
  lastError: string | null;
}

export default function Instances() {
  const { instances, stats, loading, isCacheValid, setInstances, setStats, setLoading, invalidateCache } = useInstancesStore();
  const [cacheInfo, setCacheInfo] = useState<CacheInfo | null>(null);
  const [error, setError] = useState('');
  const [selectedTab, setSelectedTab] = useState(0);

  useEffect(() => {
    const cacheIsValid = isCacheValid();
    const hasCachedData = instances.length > 0;

    if (cacheIsValid && hasCachedData) {
      return;
    }

    loadInstances();
  }, [isCacheValid, instances.length]);

  const loadInstances = async () => {
    try {
      setLoading(true);
      setError('');

      const [instancesResponse, statsResponse, cacheResponse] = await Promise.all([
        instancesApi.getInstances(),
        instancesApi.getInstanceStats(),
        cacheApi.getInfo(),
      ]);

      if (instancesResponse.success && instancesResponse.data) {
        setInstances(instancesResponse.data.instances || []);
      }

      if (statsResponse.success && statsResponse.data) {
        setStats({
          activeCount: statsResponse.data.activeCount,
          totalCount: statsResponse.data.totalCount,
          totalActiveTaskCount: statsResponse.data.totalActiveTaskCount,
        });
      }

      if (cacheResponse.success && cacheResponse.data) {
        setCacheInfo(cacheResponse.data);
      }
    } catch (err) {
      setError('加载数据失败');
      console.error('Instances load error:', err);
    } finally {
      setLoading(false);
    }
  };

  const getInstanceStatus = (instance: Instance) => {
    const now = Date.now();
    const heartbeatAge = now - instance.last_heartbeat;

    if (heartbeatAge > 2 * 60 * 1000) {
      return {
        status: 'timeout',
        text: '超时',
        color: 'text-amber-400',
        bgColor: 'bg-amber-500/[8%]',
        icon: <ClockIcon className="w-5 h-5" />,
      };
    }

    if (instance.status === 'offline') {
      return {
        status: 'offline',
        text: '离线',
        color: 'text-rose-400',
        bgColor: 'bg-rose-500/[8%]',
        icon: <XCircleIcon className="w-5 h-5" />,
      };
    }

    return {
      status: 'active',
      text: '正常',
      color: 'text-emerald-400',
      bgColor: 'bg-emerald-500/[8%]',
      icon: <CheckCircleIcon className="w-5 h-5" />,
    };
  };

  return (
    <div className="relative z-0">
      {/* Header */}
      <div className="mb-8">
        <PageHeader
          title="实例监控"
          subtitle="监控和管理系统中的所有实例"
          actions={
            <Button variant="default" onClick={() => { invalidateCache(); loadInstances(); }} disabled={loading}>
              <ArrowPathIcon className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              刷新
            </Button>
          }
        />
      </div>

      {/* Cache Info Bar */}
      {cacheInfo && (
        <SpotlightCard className="mb-8 backdrop-blur-md p-4 sm:p-6 shadow-2xl shadow-black/20 border-0" enablePhysics={true}>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-start sm:items-center space-x-4 min-w-0">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-sky-500/10 text-sky-400">
                <CircleStackIcon className="h-6 w-6" />
              </div>
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-white/20">Cache Provider / 缓存提供商</span>
                    <span className="inline-flex items-center rounded-md bg-blue-500/[8%] px-2 py-0.5 text-xs font-medium text-blue-400">
                      {cacheInfo.provider}
                    </span>
                  {cacheInfo.provider !== cacheInfo.configuredProvider && (
                      <span className="inline-flex items-center rounded-md bg-yellow-500/[8%] px-2 py-0.5 text-xs font-medium text-yellow-400">
                        配置: {cacheInfo.configuredProvider}
                      </span>
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1">
                  <span className="text-xs text-white/40">
                    列表: {cacheInfo.capabilities.canListKeys ? '✓' : '✗'}
                  </span>
                  <span className="text-xs text-white/40">
                    写入: {cacheInfo.capabilities.canWrite ? '✓' : '✗'}
                  </span>
                  {cacheInfo.lastError && (
                    <span className="text-xs text-red-400 truncate max-w-[200px]" title={cacheInfo.lastError}>
                      错误: {cacheInfo.lastError}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div className="text-xs text-gray-500 sm:text-right pt-3 sm:pt-0">
              TTL: {cacheInfo.config.defaultTtl}s | Limit: {cacheInfo.config.listLimit}
            </div>
          </div>
        </SpotlightCard>
      )}

      {/* Error Display */}
      {error && (
        <div className="mb-8 rounded-2xl bg-red-500/10 p-4 shadow-inner shadow-red-500/20">
          <div className="flex">
            <div className="flex-shrink-0">
              <XCircleIcon className="h-5 w-5 text-red-400" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-200">加载失败</h3>
              <div className="mt-2 text-sm text-red-300">
                {error}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading && instances.length === 0 ? (
        <div className="space-y-6">
          <CardSkeleton />
          <StatsGridSkeleton count={3} />
          <div className="bg-white/5 rounded-xl p-6 space-y-4">
            <div className="h-5 bg-white/10 rounded w-1/4 animate-pulse" />
            {Array.from({ length: 5 }).map((_, i) => (
              <TableRowSkeleton key={i} />
            ))}
          </div>
        </div>
      ) : (
        <TabGroup selectedIndex={selectedTab} onChange={setSelectedTab} as="div" className="w-full">
          <div className="flex justify-center mb-10">
            <div className="relative p-1 rounded-full bg-white/5 inline-flex overflow-hidden">
              {/* Sliding Indicator */}
              <div 
                className="absolute top-1 bottom-1 transition-all duration-500 ease-[cubic-bezier(0.34,1.56,0.64,1)] bg-white/10 rounded-full"
                style={{ 
                  width: 'calc(50% - 4px)', 
                  left: '2px',
                  transform: `translateX(${selectedTab * 100}%)` 
                }}
              />
              
              <TabList className="flex relative z-10">
                <Tab className="px-8 py-2.5 rounded-full text-sm font-medium leading-5 outline-none transition-all duration-500 text-white/40 data-[selected]:text-white hover:text-white/60 group whitespace-nowrap">
                  实例列表
                </Tab>
                <Tab className="px-8 py-2.5 rounded-full text-sm font-medium leading-5 outline-none transition-all duration-500 text-white/40 data-[selected]:text-white hover:text-white/60 group whitespace-nowrap">
                  统计信息
                </Tab>
              </TabList>
            </div>
          </div>

          <TabPanels as="div">
            {/* Instance List Tab */}
            <TabPanel className="outline-none transition-all duration-500 ease-[cubic-bezier(0.34,1.56,0.64,1)] data-closed:opacity-0 data-closed:translate-y-4">
              <div className="space-y-8">
                {/* Stats Cards */}
                {stats && (
                  <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
                    <SpotlightCard variant="flat" className="backdrop-blur-md overflow-hidden border-0" enablePhysics={true}>
                      <div className="p-5">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 p-3 rounded-xl bg-emerald-500/10 text-emerald-400">
                            <CheckCircleIcon className="h-6 w-6" />
                          </div>
                          <div className="ml-5 w-0 flex-1">
                            <dl>
                              <dt className="text-[10px] font-bold uppercase tracking-[0.1em] text-white/20 truncate">
                                Active Instances / 活跃实例
                              </dt>
                              <dd className="mt-1 text-3xl font-bold text-white">
                                {stats.activeCount}
                              </dd>
                            </dl>
                          </div>
                        </div>
                      </div>
                    </SpotlightCard>

                    <SpotlightCard variant="flat" className="backdrop-blur-md overflow-hidden border-0" enablePhysics={true}>
                      <div className="p-5">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 p-3 rounded-xl bg-sky-500/10 text-sky-400">
                            <ServerIcon className="h-6 w-6" />
                          </div>
                          <div className="ml-5 w-0 flex-1">
                            <dl>
                              <dt className="text-[10px] font-bold uppercase tracking-[0.1em] text-white/20 truncate">
                                Total Instances / 总实例数
                              </dt>
                              <dd className="mt-1 text-3xl font-bold text-white">
                                {stats.totalCount}
                              </dd>
                            </dl>
                          </div>
                        </div>
                      </div>
                    </SpotlightCard>

                    <SpotlightCard variant="flat" className="backdrop-blur-md overflow-hidden border-0" enablePhysics={true}>
                      <div className="p-5">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 p-3 rounded-xl bg-amber-500/10 text-amber-400">
                            <ArrowPathIcon className="h-6 w-6" />
                          </div>
                          <div className="ml-5 w-0 flex-1">
                            <dl>
                              <dt className="text-[10px] font-bold uppercase tracking-[0.1em] text-white/20 truncate">
                                Active Tasks / 总活跃任务
                              </dt>
                              <dd className="mt-1 text-3xl font-bold text-white">
                                {stats.totalActiveTaskCount}
                              </dd>
                            </dl>
                          </div>
                        </div>
                      </div>
                    </SpotlightCard>
                  </div>
                )}

                {/* Instance Cards */}
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                  {instances.map((instance) => {
                    const instanceStatus = getInstanceStatus(instance);
                     return (
                       <SpotlightCard
                         key={instance.id}
                         variant="flat"
                         className="backdrop-blur-md overflow-hidden transition-all duration-500 ease-[cubic-bezier(0.34,1.56,0.64,1)] group border-0"
                         enablePhysics={true}
                       >
                        <div className="p-6">
                          <div className="flex items-start justify-between mb-4">
                            <div className="flex items-center">
                              <div className="p-2 rounded-xl bg-indigo-500/10 text-indigo-400 mr-3 group-hover:bg-indigo-500/20 transition-all duration-500 ease-[cubic-bezier(0.34,1.56,0.64,1)]">
                                <ServerIcon className="h-6 w-6" />
                              </div>
                              <div>
                                <h3 className="text-lg font-semibold text-white">
                                  {instance.hostname}
                                </h3>
                                <p className="text-xs text-white/20 font-mono">
                                  {instance.id.substring(0, 16)}...
                                </p>
                              </div>
                            </div>
                              <span
                                className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium ${instanceStatus.color} ${instanceStatus.bgColor}`}
                              >
                                {instanceStatus.text}
                              </span>
                          </div>
                           
                          <div className="space-y-4">
                            <div className="flex justify-between items-center">
                              <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-white/20">Region / 区域</span>
                              <span className="text-sm font-medium text-white/80">{instance.region}</span>
                            </div>

                            <div className="flex justify-between items-center">
                              <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-white/20">Started / 启动时间</span>
                              <span className="text-sm font-medium text-white/80">
                                {formatDate(instance.started_at)}
                              </span>
                            </div>

                            <div className="flex justify-between items-center">
                              <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-white/20">Heartbeat / 上次心跳</span>
                              <span className="text-sm font-medium text-white/80">
                                {formatRelativeTime(instance.last_heartbeat)}
                              </span>
                            </div>

                            <div className="flex justify-between items-center">
                              <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-white/20">Latency / 延迟</span>
                              <span
                                className={`text-sm font-bold ${
                                  Date.now() - instance.last_heartbeat > 2 * 60 * 1000
                                    ? 'text-amber-400'
                                    : 'text-emerald-400'
                                }`}
                              >
                                {Math.floor((Date.now() - instance.last_heartbeat) / 1000)}s
                              </span>
                            </div>

                            <div className="flex justify-between items-center">
                              <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-white/20">Active Tasks / 活跃任务</span>
                              <span className="text-xl font-bold text-white">
                                {instance.active_task_count || 0}
                              </span>
                            </div>
                          </div>
                        </div>
                      </SpotlightCard>
                    );
                  })}
                </div>


                {instances.length === 0 && (
                  <div className="text-center py-12 bg-white/5 rounded-2xl shadow-inner shadow-white/5">
                    <ServerIcon className="mx-auto h-12 w-12 text-gray-500" />
                    <h3 className="mt-2 text-sm font-medium text-white">暂无实例</h3>
                    <p className="mt-1 text-sm text-gray-500">
                      系统中目前没有活跃的实例
                    </p>
                  </div>
                )}
              </div>
            </TabPanel>

            {/* Stats Tab */}
            <TabPanel className="outline-none transition-all duration-500 ease-[cubic-bezier(0.34,1.56,0.64,1)] data-closed:opacity-0 data-closed:translate-y-4">
              <div className="py-6">
                <SpotlightCard className="backdrop-blur-md overflow-hidden border-0" enablePhysics={true}>
                  <div className="px-4 py-5 sm:p-6">
                    <h3 className="text-lg font-semibold text-white mb-6">
                      系统统计
                    </h3>
                    {stats && (
                      <dl className="grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-2 lg:grid-cols-3">
                        <div className="p-4 rounded-xl bg-white/5">
                          <dt className="text-sm font-medium text-white/60">
                            活跃实例
                          </dt>
                          <dd className="mt-1 text-3xl font-bold text-white">
                            {stats.activeCount}
                          </dd>
                        </div>
                        <div className="p-4 rounded-xl bg-white/5">
                          <dt className="text-sm font-medium text-white/60">
                            总实例数
                          </dt>
                          <dd className="mt-1 text-3xl font-bold text-white">
                            {stats.totalCount}
                          </dd>
                        </div>
                        <div className="p-4 rounded-xl bg-white/5">
                          <dt className="text-sm font-medium text-white/60">
                            总活跃任务
                          </dt>
                          <dd className="mt-1 text-3xl font-bold text-white">
                            {stats.totalActiveTaskCount}
                          </dd>
                        </div>
                      </dl>
                    )}
                  </div>
                </SpotlightCard>
              </div>
            </TabPanel>
          </TabPanels>
        </TabGroup>
      )}
    </div>
  );
}